/**
 * @fileoverview CodeMirror 格式化和编辑工具类。
 * 所有的操作都直接针对 CodeMirror 编辑器实例 (cm)，而不是原生 DOM。
 * @typedef {import('codemirror').Editor} CodeMirrorEditor
 * @typedef {{line: number, ch: number}} Pos
 */

export class MarkdownEditorUtils {

    /**
     * @description 内部辅助方法：检查编辑器实例是否存在且处于可编辑状态
     * @param {CodeMirrorEditor} cm 
     * @returns {boolean}
     */
    static canEdit(cm) {
        // 1. 实例必须存在
        // 2. readOnly 选项必须为 false (或者 undefined/null)
        // 注意：readOnly 可能是 'nocursor' (也视为只读)
        return cm && !cm.getOption('readOnly');
    }

    /**
     * @description 从 CodeMirror 实例获取当前光标/选区信息。
     * 注意：此方法仅读取信息，不修改内容，因此允许在只读模式下调用。
     * @param {CodeMirrorEditor} cm - CodeMirror 编辑器实例
     * @returns {{
     * anchor: Pos, 
     * head: Pos, 
     * selectedText: string, 
     * selection: import('codemirror').Position
     * }}
     */
    static getSelectionInfo(cm) {
        if (!cm) return { anchor: { line: 0, ch: 0 }, head: { line: 0, ch: 0 }, selectedText: '', selection: null };

        const selectedText = cm.getSelection();
        const anchor = cm.getCursor('anchor');
        const head = cm.getCursor('head');
        
        return { 
            anchor, 
            head, 
            selectedText: selectedText, 
            selection: cm.listSelections()[0] // 获取主要的选区对象
        };
    }
    
    // ==========================================================
    // 基础 Markdown 格式化方法
    // ==========================================================

    /**
     * 加粗文本
     * @param {CodeMirrorEditor} cm - CodeMirror 编辑器实例
     */
    static handleBold(cm) {
        if (!this.canEdit(cm)) return; // <--- 检查只读状态

        const { selectedText } = this.getSelectionInfo(cm);
        
        if (selectedText) {
            let newText;
            // 检查是否已经加粗 (简单的检查)
            if (selectedText.startsWith('**') && selectedText.endsWith('**')) {
                // 解除加粗：删除两侧的 **
                newText = selectedText.substring(2, selectedText.length - 2);
            } else {
                // 加粗
                newText = `**${selectedText}**`;
            }
            
            cm.replaceSelection(newText);
        } else {
            // 无选区，插入占位符
            cm.replaceSelection('****');
            
            // 将光标定位到 ** 中间
            const cursor = cm.getCursor();
            cm.setCursor({ line: cursor.line, ch: cursor.ch - 2 });
        }

        cm.focus();
    }

    /**
     * 斜体文本
     * @param {CodeMirrorEditor} cm - CodeMirror 编辑器实例
     */
    static handleItalic(cm) {
        if (!this.canEdit(cm)) return; // <--- 检查只读状态

        const { selectedText } = this.getSelectionInfo(cm);
        
        if (selectedText) {
            // 检查是否已经是斜体 (* 但非 **)
            if (selectedText.startsWith('*') && selectedText.endsWith('*') && 
                !(selectedText.startsWith('**') || selectedText.endsWith('**'))) {
                // 解除斜体
                cm.replaceSelection(selectedText.substring(1, selectedText.length - 1));
            } else {
                // 启用斜体
                cm.replaceSelection(`*${selectedText}*`);
            }
        } else {
            // 无选区，插入占位符
            cm.replaceSelection('**');
            const cursor = cm.getCursor();
            // 将光标定位到 * 中间
            cm.setCursor({ line: cursor.line, ch: cursor.ch - 1 });
        }
        
        cm.focus();
    }
    
    /**
     * 删除线文本
     * @param {CodeMirrorEditor} cm - CodeMirror 编辑器实例
     */
    static handleStrikethrough(cm) {
        if (!this.canEdit(cm)) return; // <--- 检查只读状态

        const { selectedText } = this.getSelectionInfo(cm);
        
        if (selectedText) {
            if (selectedText.startsWith('~~') && selectedText.endsWith('~~')) {
                // 解除删除线
                cm.replaceSelection(selectedText.substring(2, selectedText.length - 2));
            } else {
                // 启用删除线
                cm.replaceSelection(`~~${selectedText}~~`);
            }
        } else {
            // 无选区，插入占位符
            cm.replaceSelection('~~~~');
            const cursor = cm.getCursor();
            // 将光标定位到 ~~ 中间
            cm.setCursor({ line: cursor.line, ch: cursor.ch - 2 });
        }
        
        cm.focus();
    }

    /**
     * 下划线文本 (Markdown 非标准，保留 HTML)
     * @param {CodeMirrorEditor} cm - CodeMirror 编辑器实例
     */
    static handleUnderline(cm) {
        if (!this.canEdit(cm)) return; // <--- 检查只读状态

        const { selectedText } = this.getSelectionInfo(cm);
        
        if (selectedText) {
            if (selectedText.startsWith('<u>') && selectedText.endsWith('</u>')) {
                // 解除下划线
                cm.replaceSelection(selectedText.substring(3, selectedText.length - 4));
            } else {
                // 启用下划线
                cm.replaceSelection(`<u>${selectedText}</u>`);
            }
        } else {
            // 无选区，插入占位符
            cm.replaceSelection('<u></u>');
            const cursor = cm.getCursor();
            // 将光标定位到 <u></u> 中间
            cm.setCursor({ line: cursor.line, ch: cursor.ch - 4 });
        }
        
        cm.focus();
    }

    /**
     * 插入链接
     * @param {CodeMirrorEditor} cm - CodeMirror 编辑器实例
     */
    static handleLink(cm) {
        if (!this.canEdit(cm)) return; // <--- 检查只读状态

        const { selectedText } = this.getSelectionInfo(cm);

        let newText;
        let startOffset;
        let endOffset;

        if (selectedText) {
            newText = `[${selectedText}](url)`;
            startOffset = 4; // 定位到 url 的 u 处
            endOffset = 1;   // 选中 url
        } else {
            newText = `[](url)`;
            startOffset = 4; // 定位到 url 的 u 处
            endOffset = 1;   // 选中 url
        }
        
        cm.replaceSelection(newText);
        
        // 选中 'url'
        const cursor = cm.getCursor();
        const endCh = cursor.ch;
        cm.setSelection(
            { line: cursor.line, ch: endCh - startOffset },
            { line: cursor.line, ch: endCh - endOffset }
        );
        
        cm.focus();
    }
    
    // ==========================================================
    // 块级结构方法
    // ==========================================================

    /**
     * 处理标题 (切换 H1 到 H6)
     * @param {CodeMirrorEditor} cm - CodeMirror 编辑器实例
     */
    static handleHeading(cm) {
        if (!this.canEdit(cm)) return; // <--- 检查只读状态
        
        const cursor = cm.getCursor();
        const lineContent = cm.getLine(cursor.line);
        let newPrefix = '';
        let currentLevel = 0;

        const match = lineContent.match(/^(#+)\s*/);
        if (match) {
            currentLevel = match[1].length;
        }

        if (currentLevel === 0) {
            // 当前无标题，切换到 H1
            newPrefix = '# ';
        } else if (currentLevel < 6) {
            // 标题等级增加
            newPrefix = '#'.repeat(currentLevel + 1) + ' ';
        } else {
            // 已经是 H6，取消标题
            newPrefix = '';
        }
        
        // 1. 获取行内容并移除原有标题符号
        let newLineContent = lineContent;
        if (match) {
            newLineContent = lineContent.substring(match[0].length);
        }
        
        // 2. 插入新的标题符号
        const finalLineContent = newPrefix + newLineContent;
        
        // 3. 替换整行内容
        cm.replaceRange(
            finalLineContent,
            { line: cursor.line, ch: 0 },
            { line: cursor.line, ch: lineContent.length }
        );

        // 4. 将光标定位到新标题后的原始位置
        cm.setCursor({ line: cursor.line, ch: newPrefix.length + cursor.ch - (match ? match[0].length : 0) });
        cm.focus();
    }

    /**
     * 插入引用 (Blockquote)
     * @param {CodeMirrorEditor} cm - CodeMirror 编辑器实例
     */
    static handleQuote(cm) {
        if (!this.canEdit(cm)) return; // <--- 检查只读状态
        
        const selection = cm.listSelections()[0];
        const startLine = selection.anchor.line;
        const endLine = selection.head.line;
        
        // 遍历选中的行
        for (let i = startLine; i <= endLine; i++) {
            let lineContent = cm.getLine(i);
            
            if (lineContent.trim().startsWith('>')) {
                // 如果已是引用，则取消引用（只移除最前面的 > ）
                const newLineContent = lineContent.replace(/^(>\s?)/, '');
                cm.replaceRange(
                    newLineContent,
                    { line: i, ch: 0 },
                    { line: i, ch: lineContent.length }
                );
            } else {
                // 添加引用符号
                const newLineContent = '> ' + lineContent;
                cm.replaceRange(
                    newLineContent,
                    { line: i, ch: 0 },
                    { line: i, ch: lineContent.length }
                );
            }
        }
        
        cm.focus();
    }
    
    /**
     * 插入水平线
     * @param {CodeMirrorEditor} cm - CodeMirror 编辑器实例
     */
    static handleHorizontalRule(cm) {
        if (!this.canEdit(cm)) return; // <--- 检查只读状态

        const cursor = cm.getCursor();
        const lineContent = cm.getLine(cursor.line);
        
        let insertText = '\n---\n';
        let insertPos = { line: cursor.line, ch: lineContent.length };

        // 如果光标不在行尾，则先移动到下一行
        if (cursor.ch !== lineContent.length) {
            insertPos = { line: cursor.line + 1, ch: 0 };
        } else if (cursor.line === 0 && lineContent.length === 0) {
             // 如果是空文件，则不加额外的换行
             insertText = '---\n';
             insertPos = { line: cursor.line, ch: 0 };
        }
        
        // 插入水平线
        cm.replaceRange(insertText, insertPos);
        
        // 将光标定位到水平线之后
        const newLineCount = (insertText.match(/\n/g) || []).length;
        cm.setCursor({ line: insertPos.line + newLineCount, ch: 0 });
        cm.focus();
    }
    
    // ==========================================================
    // 插入模板方法
    // ==========================================================

    /**
     * 插入无序列表模板
     * @param {CodeMirrorEditor} cm - CodeMirror 编辑器实例
     */
    static handleUnorderedList(cm) {
        if (!this.canEdit(cm)) return; // <--- 检查只读状态
        
        const cursor = cm.getCursor();
        
        // 生成无序列表示例
        const unorderedListExample =
            `\n- 第一项\n` +
            `- 第二项\n` +
            `- 第三项\n`;
        
        // 插入内容
        cm.replaceSelection(unorderedListExample);
        
        // 将光标定位到第一个列表项的内容位置
        const newCursorPos = { line: cursor.line + 1, ch: 2 }; // "- " 后面的位置
        cm.setCursor(newCursorPos);
        cm.focus();
    }

    /**
     * 插入有序列表模板
     * @param {CodeMirrorEditor} cm - CodeMirror 编辑器实例
     */
    static handleOrderedList(cm) {
        if (!this.canEdit(cm)) return; // <--- 检查只读状态
        
        const cursor = cm.getCursor();
        
        // 生成有序列表示例
        const orderedListExample =
            `\n1. 第一项\n` +
            `2. 第二项\n` +
            `3. 第三项\n`;
        
        // 插入内容
        cm.replaceSelection(orderedListExample);
        
        // 将光标定位到第一个列表项的内容位置
        const newCursorPos = { line: cursor.line + 1, ch: 3 }; // "1. " 后面的位置
        cm.setCursor(newCursorPos);
        cm.focus();
    }

    /**
     * 插入代码块模板
     * @param {CodeMirrorEditor} cm - CodeMirror 编辑器实例
     */
    static handleCode(cm) {
        if (!this.canEdit(cm)) return; // <--- 检查只读状态
        
        const cursor = cm.getCursor();
        
        // 生成代码块示例
        const codeBlockExample =
            `\n\`\`\`javascript\n` +
            `function helloWorld() {\n` +
            `  console.log("Hello, World!");\n` +
            `}\n` +
            `\`\`\`\n`;
        
        // 插入内容
        cm.replaceSelection(codeBlockExample);
        
        // 将光标定位到代码块内部的第一行 (function helloWorld 所在行)
        // 假设 CodeMirror 默认缩进是 2 个空格
        const newCursorPos = { line: cursor.line + 2, ch: 2 };
        cm.setCursor(newCursorPos);
        cm.focus();
    }

    /**
     * 插入表格模板
     * @param {CodeMirrorEditor} cm - CodeMirror 编辑器实例
     */
    static handleTable(cm) {
        if (!this.canEdit(cm)) return; // <--- 检查只读状态

        const cursor = cm.getCursor();
        
        const tableTemplate =
            `\n| 列1 | 列2 | 列3 |\n` +
            `| --- | --- | --- |\n` +
            `| 内容1 | 内容2 | 内容3 |\n`;
        
        cm.replaceSelection(tableTemplate);
        
        // 将光标定位到第一个内容的位置 '内容1'
        const newCursorStart = { line: cursor.line + 2, ch: 2 };
        const newCursorEnd = { line: cursor.line + 2, ch: 5 }; // 选中 '内容1'
        cm.setSelection(newCursorStart, newCursorEnd);

        cm.focus();
    }
    
    /**
     * 插入行内数学公式模板
     * @param {CodeMirrorEditor} cm - CodeMirror 编辑器实例
     */
    static handleInlineMath(cm) {
        if (!this.canEdit(cm)) return; // <--- 检查只读状态
        
        // 生成行内数学公式示例
        const mathExample = `$E = mc^2$`;
        
        cm.replaceSelection(mathExample);
        
        // 将光标定位到公式内部，选中 'E = mc^2'
        const cursor = cm.getCursor();
        const selectionLength = mathExample.length - 2; // 去掉两个 $
        
        cm.setSelection(
            { line: cursor.line, ch: cursor.ch - selectionLength - 1 }, // $ 符号后
            { line: cursor.line, ch: cursor.ch - 1 }                    // 另一个 $ 符号前
        );
        cm.focus();
    }

    /**
     * 插入块级数学公式模板
     * @param {CodeMirrorEditor} cm - CodeMirror 编辑器实例
     */
    static handleBlockMath(cm) {
        if (!this.canEdit(cm)) return; // <--- 检查只读状态
        
        const cursor = cm.getCursor();
        
        // 生成块级数学公式示例
        const blockMathExample =
            `\n$$\n` +
            `\\int_{0}^{\\infty} e^{-x^2} dx = \\frac{\\sqrt{\\pi}}{2}\n` +
            `$$\n`;
        
        cm.replaceSelection(blockMathExample);
        
        // 将光标定位到公式内部的第一行，选中公式内容
        const newCursorStart = { line: cursor.line + 1, ch: 0 };
        const newCursorEnd = { line: cursor.line + 1, ch: 45 }; // 选中公式文本
        
        cm.setSelection(newCursorStart, newCursorEnd);
        cm.focus();
    }
    
    /**
     * 插入图片模板
     * @param {CodeMirrorEditor} cm - CodeMirror 编辑器实例
     * @param {string} [imgUrl='https://example.com/image.png'] - 图片URL
     */
    static insertImg(cm, imgUrl = 'https://example.com/image.png') {
        if (!this.canEdit(cm)) return; // <--- 检查只读状态

        const imageMarkdown = `![Alt Text](${imgUrl})`;
        
        cm.replaceSelection(imageMarkdown);
        
        // 选中 'Alt Text'
        const cursor = cm.getCursor();
        const endCh = cursor.ch - (imgUrl.length + 3); // 减去 ')', '(', url 的长度
        
        cm.setSelection(
            { line: cursor.line, ch: endCh - 9 }, // 9是 'Alt Text' 的长度
            { line: cursor.line, ch: endCh }
        );

        cm.focus();
    }

    /**
     * 插入目录占位符 [TOC]
     * @param {CodeMirrorEditor} cm - CodeMirror 编辑器实例
     */
    static insertTableOfContents(cm) {
        if (!this.canEdit(cm)) return; // <--- 检查只读状态
        
        const content = cm.getValue();
        
        // 检查是否已经存在 [TOC]
        if (content.trim().startsWith('[TOC]')) {
            console.log('目录占位符 [TOC] 已存在');
            return;
        }
        
        const tocPlaceholder = '[TOC]\n\n';
        
        // 在文档开头插入
        cm.replaceRange(tocPlaceholder, { line: 0, ch: 0 });
        
        // 将光标定位在 [TOC] 后面 (第三行开头)
        cm.setCursor({ line: 2, ch: 0 }); 
        cm.focus();
    }
}